import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Star } from "lucide-react";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuthSession } from "@/hooks/useAuthSession";
import { toast } from "@/components/ui/use-toast";

import { Checkbox } from "@/components/ui/checkbox";

const Subscription = () => {
  const { t } = useTranslation('subscription');
  const { user } = useAuthSession();
  const { 
    subscription, 
    loading, 
    openCustomerPortal,
    isTrialActive,
    trialDaysRemaining,
    hasPremiumAccess 
  } = useSubscription();
  
  const [promoCode, setPromoCode] = useState("");
  const [isAnnual, setIsAnnual] = useState(false);
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const STRIPE_URL_MONTHLY = 'https://buy.stripe.com/4gMbJ10Ke283e2C3In4gg00';
  const STRIPE_URL_ANNUAL = 'https://buy.stripe.com/8x2bJ1akO5kf8Ii5Qv4gg01';

  const handleSubscribe = async (planType: 'monthly' | 'annual') => {
    if (!gdprAccepted) {
      toast({
        title: t('gdpr.consentRequired'),
        description: t('gdpr.consentDescription'),
        variant: "destructive",
      });
      return;
    }

    const url = planType === 'monthly' ? STRIPE_URL_MONTHLY : STRIPE_URL_ANNUAL;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const features = [
    t('features.0'),
    t('features.1'),
    t('features.2'),
    t('features.3'),
    t('features.4'),
    t('features.5')
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationMenu />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">{t('loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationMenu />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary mb-4">
            {t('title')}
          </h1>
          <p className="text-xl text-muted-foreground">
            {t('subtitle')}
          </p>
        </div>

        {/* Current Status */}
        {user ? (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                {t('currentStatus.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {subscription.is_beta_tester ? (
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                      <Crown className="w-4 h-4 mr-1" />
                      {t('currentStatus.betaTester')}
                    </Badge>
                  ) : subscription.subscribed ? (
                    <Badge variant="default" className="bg-green-100 text-green-800">
                      {t('currentStatus.subscribed')} - {subscription.subscription_tier}
                    </Badge>
                  ) : isTrialActive ? (
                    <Badge variant="outline" className="bg-blue-100 text-blue-800">
                      {t('currentStatus.trial')} - {trialDaysRemaining} {t('currentStatus.daysRemaining')}
                    </Badge>
                  ) : (
                    <Badge variant="outline">{t('currentStatus.freeAccess')}</Badge>
                  )}
                </div>
                
                <Button asChild variant="default" size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                  <a href="https://payment.golf-progress-calculator.com/p/login/4gMbJ10Ke283e2C3In4gg00" target="_blank" rel="noopener noreferrer">
                    {t('currentStatus.manageSubscription')}
                  </a>
                </Button>
              </div>
              
              {isTrialActive && (
                <p className="text-sm text-muted-foreground mt-2">
                  {t('currentStatus.trialEnding', { days: trialDaysRemaining })}
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-8 border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-800">
                <Star className="w-5 h-5" />
                Statut de l'abonnement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
                    Connexion requise
                  </Badge>
                  <p className="text-sm text-orange-700">
                    Connectez-vous pour voir votre statut d'abonnement
                  </p>
                </div>
                
                <Button asChild variant="default" size="sm">
                  <a href="/auth">Se connecter</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Plans - Show if not a paying subscriber */}
        {!subscription.subscribed && !subscription.is_beta_tester && (
          <>
            <div className="mb-6 flex items-start gap-3">
              <Checkbox id="gdpr-consent" checked={gdprAccepted} onCheckedChange={(checked) => setGdprAccepted(Boolean(checked))} />
              <label htmlFor="gdpr-consent" className="text-sm text-muted-foreground">
                J’accepte la Politique de confidentialité et les Conditions, et j’autorise le traitement de mes données pour la gestion de l’abonnement.
                <a href="/gdpr" className="underline"> {t('gdpr.learnMore')}</a>.
              </label>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Monthly Plan */}
            <Card className={`relative ${!isAnnual ? 'ring-2 ring-primary' : ''}`}>
              <CardHeader>
                <CardTitle className="text-2xl">{t('pricing.monthlyPlan')}</CardTitle>
                <CardDescription>{t('pricing.monthlyDescription')}</CardDescription>
                <div className="text-3xl font-bold">{t('pricing.monthlyPrice')}<span className="text-lg font-normal">{t('pricing.monthlyPeriod')}</span></div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => handleSubscribe('monthly')} 
                  className="w-full"
                  variant={!isAnnual ? "default" : "outline"}
                  disabled={!gdprAccepted}
                >
                  {t('pricing.chooseMonthly')}
                </Button>
              </CardContent>
            </Card>

            {/* Annual Plan */}
            <Card className={`relative ${isAnnual ? 'ring-2 ring-primary' : ''}`}>
              <CardHeader>
                <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                  {t('pricing.savings')}
                </div>
                <CardTitle className="text-2xl">{t('pricing.annualPlan')}</CardTitle>
                <CardDescription>{t('pricing.annualDescription')}</CardDescription>
                <div className="text-3xl font-bold">
                  {t('pricing.annualPrice')}<span className="text-lg font-normal">{t('pricing.annualPeriod')}</span>
                  <div className="text-sm text-muted-foreground line-through">{t('pricing.originalPrice')}</div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => handleSubscribe('annual')} 
                  className="w-full"
                  variant={isAnnual ? "default" : "outline"}
                  disabled={!gdprAccepted}
                >
                  {t('pricing.chooseAnnual')}
                </Button>
              </CardContent>
            </Card>
          </div>
          </>
        )}

        {/* Promo Code */}
        {!subscription.subscribed && !subscription.is_beta_tester && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{t('promoCode.title')}</CardTitle>
              <CardDescription>
                {t('promoCode.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  placeholder={t('promoCode.placeholder')}
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  className="flex-1"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trial Information */}
        {!user && (
          <Card>
            <CardHeader>
              <CardTitle>{t('trial.title')}</CardTitle>
              <CardDescription>
                {t('trial.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {t('trial.details')}
              </p>
              <Button asChild>
                <a href="/auth">{t('trial.startTrial')}</a>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Subscription;